import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Checkbox,
  FormControl,
  InputLabel,
  Tooltip,
  Menu,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
} from '@mui/material';
import {
  Public as PublicIcon,
  Bolt as BoltIcon,
  Stop as StopIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Terminal as TerminalIcon,
  Edit as EditIcon,
  History as HistoryIcon,
} from '@mui/icons-material';

import {
  startDomainGeneration,
  getDomainTaskStatus,
  stopDomainGeneration,
  getDomainResults,
  deleteDomain,
  updateDomainsCategory,
  removeNonFavoriteDomains,
  addDomainFavorite,
  removeDomainFavorite,
  getDomainCategories,
  createDomainCategory,
  renameDomainCategory,
  type DomainResultItem,
  type DomainCategory,
} from '../../services/domainFinderService';

const DEFAULT_TLDS = ['.com', '.net', '.org', '.io', '.co', '.ai', '.app', '.dev'];

const HISTORY_KEY = 'domain-finder-history';
const MAX_HISTORY = 20;

const ALL_CATEGORIES = '__all__';
const UNCATEGORIZED = '__uncategorized__';

interface SearchHistoryEntry {
  description: string;
  tlds: string;
  maxLength: number;
  quantity: number;
  category: string;
  timestamp: string;
}

function loadHistory(): SearchHistoryEntry[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveToHistory(entry: Omit<SearchHistoryEntry, 'timestamp'>) {
  const history = loadHistory();
  if (
    history.length > 0 &&
    history[0].description === entry.description &&
    history[0].tlds === entry.tlds
  ) {
    return;
  }
  history.unshift({ ...entry, timestamp: new Date().toISOString() });
  if (history.length > MAX_HISTORY) history.length = MAX_HISTORY;
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
}

const DomainFinder: React.FC = () => {
  // Form state
  const [description, setDescription] = useState('');
  const [selectedTlds, setSelectedTlds] = useState<string[]>(['.com']);
  const [customTlds, setCustomTlds] = useState('.com');
  const [maxLength, setMaxLength] = useState(20);
  const [quantity, setQuantity] = useState(20);
  const [categoryFilter, setCategoryFilter] = useState<string>(ALL_CATEGORIES);
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [, setTaskId] = useState<string | null>(null);
  const [statusMessages, setStatusMessages] = useState<string[]>([]);
  const [statusVisible, setStatusVisible] = useState(false);
  const [foundCount, setFoundCount] = useState(0);
  const [targetCount, setTargetCount] = useState(0);

  // Data
  const [allDomains, setAllDomains] = useState<DomainResultItem[]>([]);
  const [categories, setCategories] = useState<DomainCategory[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Category dialogs
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showRenameCategory, setShowRenameCategory] = useState(false);
  const [renameCategoryName, setRenameCategoryName] = useState('');

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    message: string;
    onConfirm: () => void;
  } | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    message: string;
    severity: 'success' | 'error' | 'info';
  } | null>(null);

  // Search inside results
  const [domainSearch, setDomainSearch] = useState('');
  const [copiedDomain, setCopiedDomain] = useState<string | null>(null);

  // History dropdown
  const [searchHistory, setSearchHistory] = useState<SearchHistoryEntry[]>([]);
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchHistory(loadHistory());
  }, []);

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info' = 'info'
  ) => {
    setSnackbar({ message, severity });
  };

  // Load data on mount
  useEffect(() => {
    loadCategories();
    loadAllResults();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const loadCategories = async () => {
    try {
      const data = await getDomainCategories();
      setCategories(data.categories);
    } catch (e) {
      console.error('Failed to load categories:', e);
    }
  };

  const loadAllResults = async () => {
    try {
      const data = await getDomainResults();
      setAllDomains(data.domains);
    } catch (e) {
      console.error('Failed to load results:', e);
    }
  };

  // TLD sync
  const toggleTld = (tld: string) => {
    setSelectedTlds((prev) => {
      const next = prev.includes(tld) ? prev.filter((t) => t !== tld) : [...prev, tld];
      setCustomTlds(next.join(','));
      return next;
    });
  };

  useEffect(() => {
    const vals = customTlds
      .split(',')
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean);
    setSelectedTlds(vals);
  }, [customTlds]);

  // Category change handler — preload last_description
  const handleCategoryChange = useCallback(
    (value: string) => {
      setCategoryFilter(value);
      if (value !== ALL_CATEGORIES && value !== UNCATEGORIZED) {
        const cat = categories.find((c) => c.name === value);
        if (cat?.last_description) {
          setDescription(cat.last_description);
        }
      }
    },
    [categories]
  );

  const effectiveFilter =
    categoryFilter === ALL_CATEGORIES ? '' : categoryFilter;

  const searchLower = domainSearch.toLowerCase();
  const filteredDomains = useMemo(
    () =>
      allDomains.filter((d) => {
        if (effectiveFilter === UNCATEGORIZED) {
          if (d.category) return false;
        } else if (effectiveFilter) {
          if (d.category !== effectiveFilter) return false;
        }
        if (favoritesOnly && !d.is_favorite) return false;
        if (searchLower && !d.domain.toLowerCase().includes(searchLower)) return false;
        return true;
      }),
    [allDomains, effectiveFilter, favoritesOnly, searchLower]
  );

  const grouped: Record<string, DomainResultItem[]> = {};
  filteredDomains.forEach((d) => {
    const cat = d.category || 'Uncategorized';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(d);
  });
  const sortedCategories = Object.keys(grouped).sort();
  const favCount = filteredDomains.filter((d) => d.is_favorite).length;

  const canGenerate =
    description.trim() !== '' && categoryFilter !== UNCATEGORIZED;

  // Generation
  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canGenerate) return;

    setGenerating(true);
    setStatusVisible(true);
    setStatusMessages(['Starting domain generation...']);
    setFoundCount(0);
    setTargetCount(quantity);

    saveToHistory({
      description,
      tlds: selectedTlds.join(','),
      maxLength,
      quantity,
      category: effectiveFilter || '',
    });
    setSearchHistory(loadHistory());

    try {
      const data = await startDomainGeneration({
        description,
        tlds: selectedTlds.join(','),
        max_length: maxLength,
        quantity_needed: quantity,
        category: effectiveFilter || undefined,
      });
      if (data.task_id) {
        setTaskId(data.task_id);
        pollRef.current = setInterval(() => pollStatus(data.task_id), 2000);
      }
    } catch (err) {
      setGenerating(false);
      showSnackbar('Failed to start generation', 'error');
    }
  };

  const pollStatus = async (tid: string) => {
    try {
      const data = await getDomainTaskStatus(tid);
      if (data.status_messages?.length) {
        setStatusMessages(data.status_messages);
      }
      if (data.domains?.length) {
        setFoundCount(data.domains.length);
        setAllDomains((prev) => {
          const existing = new Set(prev.map((d) => d.domain));
          const newOnes = data.domains
            .filter((d) => !existing.has(d.toLowerCase()))
            .map((d) => ({
              domain: d.toLowerCase(),
              category: effectiveFilter || null,
              is_favorite: false,
              created_at: new Date().toISOString(),
            }));
          return [...prev, ...newOnes];
        });
      }
      if (data.done) {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setGenerating(false);
        setTaskId(null);
        loadAllResults();
      }
    } catch {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      setGenerating(false);
      setTaskId(null);
    }
  };

  const handleStop = async () => {
    try {
      const data = await stopDomainGeneration();
      if (data.success) {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setGenerating(false);
        setTaskId(null);
        showSnackbar(`Stopped ${data.stopped_count} task(s)`, 'success');
        loadAllResults();
      }
    } catch {
      showSnackbar('Failed to stop', 'error');
    }
  };

  // Domain actions
  const handleToggleFavorite = async (domain: string, isFav: boolean) => {
    try {
      if (isFav) {
        await removeDomainFavorite(domain);
      } else {
        await addDomainFavorite(domain);
      }
      setAllDomains((prev) =>
        prev.map((d) => (d.domain === domain ? { ...d, is_favorite: !isFav } : d))
      );
    } catch {
      showSnackbar('Failed to update favorite', 'error');
    }
  };

  const handleCopyDomain = (domain: string) => {
    navigator.clipboard.writeText(domain).then(() => {
      setCopiedDomain(domain);
      setTimeout(() => setCopiedDomain(null), 1000);
    });
  };

  const handleDeleteDomain = async (domain: string) => {
    try {
      await deleteDomain(domain);
      setAllDomains((prev) => prev.filter((d) => d.domain !== domain));
      showSnackbar(`Deleted ${domain}`, 'success');
    } catch {
      showSnackbar('Error deleting', 'error');
    }
  };

  const handleCopyAll = () => {
    const text = filteredDomains.map((d) => d.domain).join('\n');
    if (text) {
      navigator.clipboard
        .writeText(text)
        .then(() =>
          showSnackbar(`Copied ${filteredDomains.length} domains`, 'success')
        );
    }
  };

  const handleCopyFavorites = () => {
    const favs = filteredDomains.filter((d) => d.is_favorite).map((d) => d.domain);
    if (favs.length) {
      navigator.clipboard
        .writeText(favs.join('\n'))
        .then(() => showSnackbar(`Copied ${favs.length} favorites`, 'success'));
    } else {
      showSnackbar('No favorites to copy', 'info');
    }
  };

  const handleDeleteNonFavorites = () => {
    const cat = effectiveFilter || null;
    const nonFavCount = filteredDomains.filter((d) => !d.is_favorite).length;
    if (nonFavCount === 0) {
      showSnackbar('No non-favorites to delete', 'info');
      return;
    }
    setConfirmDialog({
      message: `Delete ${nonFavCount} non-favorite domain${nonFavCount > 1 ? 's' : ''}?`,
      onConfirm: async () => {
        try {
          const data = await removeNonFavoriteDomains(cat);
          showSnackbar(`Deleted ${data.removed_count} domains`, 'success');
          loadAllResults();
        } catch {
          showSnackbar('Error deleting', 'error');
        }
      },
    });
  };

  const toggleSelect = (domain: string) => {
    setSelectedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (selectedDomains.size === 0) return;
    const count = selectedDomains.size;
    setConfirmDialog({
      message: `Delete ${count} selected domain${count > 1 ? 's' : ''}?`,
      onConfirm: async () => {
        try {
          await Promise.all(Array.from(selectedDomains).map((d) => deleteDomain(d)));
          setSelectedDomains(new Set());
          loadAllResults();
          showSnackbar(`Deleted ${count} domains`, 'success');
        } catch {
          showSnackbar('Error deleting', 'error');
        }
      },
    });
  };

  const handleFavoriteSelected = async () => {
    if (selectedDomains.size === 0) return;
    try {
      await Promise.all(
        Array.from(selectedDomains).map((d) => addDomainFavorite(d))
      );
      setAllDomains((prev) =>
        prev.map((d) =>
          selectedDomains.has(d.domain) ? { ...d, is_favorite: true } : d
        )
      );
      showSnackbar(`Favorited ${selectedDomains.size} domains`, 'success');
      setSelectedDomains(new Set());
    } catch {
      showSnackbar('Error updating favorites', 'error');
    }
  };

  const handleSelectAll = () => {
    setSelectedDomains(new Set(filteredDomains.map((d) => d.domain)));
  };

  const handleMoveSelected = async (cat: string) => {
    if (!cat || selectedDomains.size === 0) return;
    try {
      await updateDomainsCategory(Array.from(selectedDomains), cat);
      const count = selectedDomains.size;
      setSelectedDomains(new Set());
      loadAllResults();
      showSnackbar(`Moved ${count} domains to "${cat}"`, 'success');
    } catch {
      showSnackbar('Error updating', 'error');
    }
  };

  // Category creation
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await createDomainCategory(newCategoryName.trim());
      await loadCategories();
      setCategoryFilter(newCategoryName.trim());
      setShowNewCategory(false);
      setNewCategoryName('');
      showSnackbar(`Created "${newCategoryName.trim()}"`, 'success');
    } catch {
      showSnackbar('Failed to create category', 'error');
    }
  };

  const handleRenameCategory = async () => {
    if (!renameCategoryName.trim() || !effectiveFilter) return;
    const cat = categories.find((c) => c.name === effectiveFilter);
    if (!cat) return;
    try {
      await renameDomainCategory(cat.id, renameCategoryName.trim());
      const newName = renameCategoryName.trim();
      await loadCategories();
      await loadAllResults();
      setCategoryFilter(newName);
      setShowRenameCategory(false);
      setRenameCategoryName('');
      showSnackbar(`Renamed to "${newName}"`, 'success');
    } catch {
      showSnackbar('Failed to rename category', 'error');
    }
  };

  const openRenameCategory = () => {
    setRenameCategoryName(effectiveFilter);
    setShowRenameCategory(true);
  };

  const toggleGroup = (cat: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  // Scroll status to bottom
  useEffect(() => {
    if (statusRef.current) {
      statusRef.current.scrollTop = statusRef.current.scrollHeight;
    }
  }, [statusMessages]);

  const statusMsgColor = (msg: string) => {
    if (msg.includes('AVAILABLE')) return '#34d399';
    if (msg.includes('Checking')) return '#60a5fa';
    if (msg.includes('Completed') || msg.includes('Target reached')) return '#86efac';
    if (msg.includes('Stopped') || msg.includes('Failed') || msg.includes('Error'))
      return '#f87171';
    return '#a1a1aa';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        height: 'calc(100vh - 64px)',
        // Break out of UserLayout's p:3 + alignItems:'flex-start' so the
        // results panel hugs the right edge and the form fills the rest.
        alignSelf: 'stretch',
        mx: -3,
        mt: -3,
        mb: -3,
        width: 'auto',
      }}
    >
      {/* Left: Form & Status */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: 'primary.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PublicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Domain Finder
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 500 }}
            >
              Category
            </Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={categoryFilter}
                onChange={(e) => handleCategoryChange(e.target.value as string)}
              >
                <MenuItem value={ALL_CATEGORIES}>All Categories</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.name}>
                    {c.name}
                  </MenuItem>
                ))}
                <MenuItem value={UNCATEGORIZED}>Uncategorized</MenuItem>
              </Select>
            </FormControl>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowNewCategory(true)}
            >
              New
            </Button>
            {effectiveFilter && effectiveFilter !== UNCATEGORIZED && (
              <Tooltip title="Rename category">
                <IconButton size="small" onClick={openRenameCategory}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Divider orientation="vertical" flexItem />
            <Chip
              size="small"
              label={
                generating
                  ? `Found ${foundCount} of ${targetCount}`
                  : 'Ready'
              }
              color={generating ? 'warning' : 'default'}
              variant={generating ? 'filled' : 'outlined'}
            />
          </Box>
        </Box>

        {/* Form */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
          <Box
            component="form"
            onSubmit={handleGenerate}
            sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}
          >
            {/* ── Description ── */}
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Describe what you're building
                </Typography>
                {searchHistory.length > 0 && (
                  <Button
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={(e) => setHistoryAnchor(e.currentTarget)}
                    sx={{ color: 'text.secondary' }}
                  >
                    Recent
                  </Button>
                )}
                <Menu
                  anchorEl={historyAnchor}
                  open={!!historyAnchor}
                  onClose={() => setHistoryAnchor(null)}
                  PaperProps={{ sx: { maxHeight: 320, width: 360 } }}
                >
                  {searchHistory.map((entry, i) => (
                    <MenuItem
                      key={i}
                      onClick={() => {
                        setDescription(entry.description);
                        setCustomTlds(entry.tlds);
                        setSelectedTlds(entry.tlds.split(',').filter(Boolean));
                        setMaxLength(entry.maxLength);
                        setQuantity(entry.quantity);
                        if (entry.category) setCategoryFilter(entry.category);
                        setHistoryAnchor(null);
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        py: 1,
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ width: '100%' }}>
                        {entry.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {entry.tlds} · qty {entry.quantity} ·{' '}
                        {new Date(entry.timestamp).toLocaleDateString()}
                      </Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
              <TextField
                multiline
                minRows={8}
                maxRows={20}
                fullWidth
                placeholder="A luxury travel agency specializing in honeymoons, destination weddings, and romantic getaways to Europe and the Caribbean..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Box>

            {/* ── TLDs ── */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                Domain extensions
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                {DEFAULT_TLDS.map((tld) => {
                  const selected = selectedTlds.includes(tld);
                  return (
                    <Chip
                      key={tld}
                      label={tld}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => toggleTld(tld)}
                      sx={{ cursor: 'pointer', fontFamily: 'monospace' }}
                    />
                  );
                })}
                <TextField
                  size="small"
                  label="Add custom"
                  placeholder=".xyz,.tech"
                  value={customTlds}
                  onChange={(e) => setCustomTlds(e.target.value)}
                  sx={{
                    width: 220,
                    ml: 1.5,
                    '& input': { fontFamily: 'monospace' },
                  }}
                />
              </Box>
            </Box>

            {/* ── Tuning + CTA ── */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                pt: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
                mt: 0.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  size="small"
                  label="Max length"
                  type="number"
                  inputProps={{ min: 5, max: 63 }}
                  value={maxLength}
                  onChange={(e) => setMaxLength(Number(e.target.value))}
                  sx={{ width: 130 }}
                />
                <TextField
                  size="small"
                  label="How many"
                  type="number"
                  inputProps={{ min: 1, max: 20 }}
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                  sx={{ width: 130 }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 16 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip
                  title={
                    !description.trim()
                      ? 'Enter a description first'
                      : 'Generate domain names'
                  }
                >
                  <span>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={!canGenerate || generating}
                      startIcon={
                        generating ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <BoltIcon />
                        )
                      }
                      sx={{ minWidth: 220, py: 1.25 }}
                    >
                      {generating
                        ? `Finding domains  ${foundCount}/${targetCount}`
                        : 'Find available domains'}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Stop generation">
                  <span>
                    <IconButton
                      color="error"
                      disabled={!generating}
                      onClick={handleStop}
                    >
                      <StopIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* Status tray */}
            {statusVisible && (
              <Paper
                variant="outlined"
                sx={{
                  overflow: 'hidden',
                  bgcolor: '#18181b',
                  color: '#e4e4e7',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 0.75,
                    bgcolor: '#27272a',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: '#a1a1aa',
                    }}
                  >
                    <TerminalIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      Status
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      sx={{ color: '#a1a1aa' }}
                      onClick={() => {
                        const text = statusMessages.join('\n');
                        if (text) navigator.clipboard.writeText(text);
                      }}
                    >
                      <CopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ color: '#a1a1aa' }}
                      onClick={() => setStatusMessages([])}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Box>
                <Box
                  ref={statusRef}
                  sx={{
                    px: 1.5,
                    py: 1,
                    maxHeight: 240,
                    overflowY: 'auto',
                    fontFamily: 'monospace',
                    fontSize: 11,
                    lineHeight: 1.6,
                  }}
                >
                  {statusMessages.map((msg, i) => (
                    <Box key={i} sx={{ color: statusMsgColor(msg) }}>
                      {msg}
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
      </Box>

      {/* Right: Results */}
      <Box
        sx={{
          width: { xs: '100%', lg: 380 },
          flexShrink: 0,
          borderLeft: { lg: '1px solid' },
          borderColor: { lg: 'divider' },
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Sidebar header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Results
            </Typography>
            <Stack direction="row" spacing={0.75}>
              <Chip
                label={filteredDomains.length}
                size="small"
                variant="filled"
              />
              <Chip
                size="small"
                variant="outlined"
                icon={<StarIcon sx={{ fontSize: 12 }} />}
                label={favCount}
                sx={{ color: '#d97706', borderColor: '#fcd34d' }}
              />
            </Stack>
          </Box>
          <TextField
            size="small"
            fullWidth
            placeholder="Search domains..."
            value={domainSearch}
            onChange={(e) => setDomainSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PublicIcon sx={{ fontSize: 16 }} />
                </InputAdornment>
              ),
              endAdornment: domainSearch ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setDomainSearch('')}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ mb: 1 }}
          />
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Button
              size="small"
              variant={favoritesOnly ? 'contained' : 'text'}
              color="warning"
              startIcon={favoritesOnly ? <StarIcon /> : <StarBorderIcon />}
              onClick={() => setFavoritesOnly((v) => !v)}
              sx={{ minWidth: 0 }}
            >
              Favorites only
            </Button>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
            <Button
              size="small"
              startIcon={<CopyIcon />}
              onClick={handleCopyAll}
              sx={{ minWidth: 0, color: 'text.secondary' }}
            >
              Copy all
            </Button>
            <Button
              size="small"
              startIcon={<StarIcon />}
              onClick={handleCopyFavorites}
              sx={{ minWidth: 0, color: '#d97706' }}
            >
              Copy favs
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteNonFavorites}
              sx={{ minWidth: 0, color: 'text.secondary' }}
            >
              Del non-favs
            </Button>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
            <Button
              size="small"
              startIcon={
                selectedDomains.size === filteredDomains.length &&
                filteredDomains.length > 0 ? (
                  <CloseIcon />
                ) : (
                  <CheckIcon />
                )
              }
              onClick={
                selectedDomains.size === filteredDomains.length &&
                filteredDomains.length > 0
                  ? () => setSelectedDomains(new Set())
                  : handleSelectAll
              }
              disabled={filteredDomains.length === 0}
              sx={{ minWidth: 0, color: 'text.secondary' }}
            >
              {selectedDomains.size === filteredDomains.length &&
              filteredDomains.length > 0
                ? 'Unselect all'
                : 'Select all'}
            </Button>
          </Box>
        </Box>

        {/* Selection actions */}
        {selectedDomains.size > 0 && (
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: 'primary.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: 'primary.main' }}
            >
              {selectedDomains.size} selected
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FormControl size="small">
                <Select
                  value=""
                  displayEmpty
                  onChange={(e) => handleMoveSelected(e.target.value as string)}
                  sx={{ minWidth: 110, height: 28, fontSize: 12 }}
                >
                  <MenuItem value="" disabled>
                    Move to...
                  </MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.name}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Favorite selected">
                <IconButton
                  size="small"
                  sx={{ color: '#d97706' }}
                  onClick={handleFavoriteSelected}
                >
                  <StarIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete selected">
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDeleteSelected}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear selection">
                <IconButton
                  size="small"
                  onClick={() => setSelectedDomains(new Set())}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Domain list */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
          {filteredDomains.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <PublicIcon sx={{ fontSize: 36, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">
                {allDomains.length === 0
                  ? 'No domains yet. Generate some above.'
                  : 'No domains match your filters.'}
              </Typography>
            </Box>
          ) : (
            sortedCategories.map((cat) => {
              const catDomains = grouped[cat]
                .slice()
                .sort((a, b) => a.domain.localeCompare(b.domain));
              const isCollapsed = collapsedGroups.has(cat);
              return (
                <Box key={cat} sx={{ mb: 1 }}>
                  <Box
                    onClick={() => toggleGroup(cat)}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1,
                      py: 0.75,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: 'text.secondary' }}
                      >
                        {cat}
                      </Typography>
                      <Chip
                        label={catDomains.length}
                        size="small"
                        sx={{ height: 16, fontSize: 10 }}
                      />
                    </Box>
                    {isCollapsed ? (
                      <ChevronRightIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 16 }} />
                    )}
                  </Box>
                  {!isCollapsed && (
                    <Box sx={{ mt: 0.5 }}>
                      {catDomains.map((d) => (
                        <Box
                          key={d.domain}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: selectedDomains.has(d.domain)
                              ? 'primary.50'
                              : d.is_favorite
                              ? 'rgba(252, 211, 77, 0.08)'
                              : 'transparent',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={selectedDomains.has(d.domain)}
                            onChange={() => toggleSelect(d.domain)}
                            sx={{ p: 0.25 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              flex: 1,
                              fontFamily: 'monospace',
                              fontSize: 12,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {d.domain}
                          </Typography>
                          <Tooltip title={d.is_favorite ? 'Unfavorite' : 'Favorite'}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleFavorite(d.domain, d.is_favorite)
                              }
                              sx={{
                                p: 0.25,
                                color: d.is_favorite ? '#d97706' : 'text.disabled',
                              }}
                            >
                              {d.is_favorite ? (
                                <StarIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <StarBorderIcon sx={{ fontSize: 14 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyDomain(d.domain)}
                              sx={{ p: 0.25, color: 'text.disabled' }}
                            >
                              {copiedDomain === d.domain ? (
                                <CheckIcon
                                  sx={{ fontSize: 14, color: '#10b981' }}
                                />
                              ) : (
                                <CopyIcon sx={{ fontSize: 14 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteDomain(d.domain)}
                              sx={{ p: 0.25, color: 'text.disabled' }}
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      {/* New Category Dialog */}
      <Dialog
        open={showNewCategory}
        onClose={() => setShowNewCategory(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="Category name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewCategory(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCategory}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Category Dialog */}
      <Dialog
        open={showRenameCategory}
        onClose={() => setShowRenameCategory(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="New name"
            value={renameCategoryName}
            onChange={(e) => setRenameCategoryName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRenameCategory()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRenameCategory(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRenameCategory}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog
        open={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmDialog?.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              confirmDialog?.onConfirm();
              setConfirmDialog(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(null)}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default DomainFinder;
